import IdHelper from '../../Core/helper/IdHelper.js';
import DomSync from '../../Core/helper/DomSync.js';
import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import Delayable from '../../Core/mixin/Delayable.js';
import DateHelper from '../../Core/helper/DateHelper.js';

/**
 * @module Gantt/feature/ProgressLine
 */

/**
 *
 * This feature draws project progress line with SVG lines. Requires {@link SchedulerPro/feature/PercentBar} to be enabled (which
 * by default, it is)
 *
 * This feature is **disabled** by default.
 * For info on enabling it, see {@link Grid/view/mixin/GridFeatures}.
 *
 * ```javascript
 * let gantt = new Gantt({
 *     features : {
 *         progressLine : {
 *            statusDate : new Date(2017, 2, 8)
 *         }
 *     }
 * });
 * ```
 *
 * Status date can be changed dynamically:
 *
 * ```javascript
 * gantt.features.progressLine.statusDate = new Date();
 * ```
 *
 * If status date is not provided, a project's {@link Gantt.model.ProjectModel#field-statusDate} is used.
 *
 * If status date is not in the current Gantt time span, progress line will use view start or end coordinates. This
 * behavior can be customized with {@link #config-drawLineOnlyWhenStatusDateVisible} config. Or you can override {@link #function-shouldDrawProgressLine}
 * method and provide more complex condition.
 *
 * Progress line is a set of SVG <line> elements drawn between all the tasks.
 *
 * {@inlineexample Gantt/feature/ProgressLine.js}
 *
 * @demo Gantt/progressline
 * @extends Core/mixin/InstancePlugin
 * @mixes Core/mixin/Delayable
 * @classtype progressLine
 * @feature
 */
export default class ProgressLine extends Delayable(InstancePlugin) {
    /**
     * Fired when progress line is rendered
     * @event progressLineDrawn
     */

    //region Config

    static $name = 'ProgressLine';

    static configurable = {
        /**
         * A reference date, to track the progress from. If not provided, the project's {@link Gantt.model.ProjectModel#field-statusDate status date} is used.
         * @config {Date}
         */
        statusDate : undefined,

        /**
         * Set to `true` to hide progress line, when status date is not in the current time axis.
         * @config {Boolean}
         */
        drawLineOnlyWhenStatusDateVisible : false,

        lineCls : 'b-gantt-progress-line',

        containerCls : 'b-progress-line-canvas',

        /**
         * Vertical pixel offset for connecting point at parent task containers, to keep lines visually consistent.
         * Adjust this value when the task containers are styled differently from default themes.
         * Negative value moves the connecting point up, positive value moves it down.
         * @config {Number}
         * @default -4
         */
        parentOffsetY : -4
    };

    static get pluginConfig() {
        return {
            chain : ['onInternalPaint']
        };
    }

    domConfigs = new Map();

    //endregion

    //region Init & destroy

    construct(client, config = {}) {
        const me = this;

        // Many things may schedule a draw. Ensure it only happens once, on the next frame.
        // And Ensure it really is on the *next* frame after invocation by passing
        // the cancelOutstanding flag.
        me.scheduleDraw = me.createOnFrame('draw', [], me, true);

        super.construct(client, config);

        this.lineSegments = [];
    }

    doDisable(disable) {
        const me = this;

        super.doDisable(disable);

        // attach/detach listeners
        me.attachToClient(disable ? null : me.client);

        if (me.client.rendered) {
            if (disable) {
                me.clearDomConfigs();
                me.domSync();
            }
            else {
                me.draw();
            }
        }

    }

    //endregion

    /**
     * Progress line status date. If not provided, current date is used.
     * @member {Date} statusDate
     */
    get statusDate() {
        return this._statusDate || this.client.project.statusDate;
    }

    changeStatusDate(date) {
        if (date instanceof Date) {
            return date;
        }
    }

    updateStatusDate(date) {
        if (!this.disabled) {
            this.scheduleDraw();
        }
    }

    get svgCanvas() {
        const
            me = this,
            { client } = me;

        if (!me._svgCanvas) {
            const svg = me._svgCanvas = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

            svg.setAttribute('id', IdHelper.generateId('svg'));
            svg.classList.add('b-sch-canvas', me.containerCls);
            // To not be touched when syncing tasks to DOM
            svg.retainElement = true;
            client.timeAxisSubGridElement.appendChild(svg);
        }
        return me._svgCanvas;
    }

    // region Event handlers

    attachToProject(project) {
        this.detachListeners('project');

        project?.ion({
            name             : 'project',
            statusDateChange : 'onProjectStatusDateChange',
            refresh          : 'onProjectRefresh',
            thisObj          : this
        });
    }

    attachToRowManager(rowManager) {
        this.detachListeners('rowManager');

        rowManager?.ion({
            name              : 'rowManager',
            translaterow      : 'onTranslateRow',
            refresh           : 'scheduleDraw',
            rerender          : 'scheduleDraw',
            changetotalheight : 'scheduleDraw',
            thisObj           : this
        });
    }

    attachToClient(client) {
        const me = this;

        me.detachListeners('client');

        client?.ion({
            name                   : 'client',
            togglenode             : 'scheduleDraw',
            taskdrag               : 'onTaskDrag',
            taskdragabortfinalized : 'scheduleDraw',
            aftertaskdrop          : 'scheduleDraw',
            timelineviewportresize : 'scheduleDraw',
            thisObj                : me
        });

        me.attachToProject(client?.project);
        me.attachToRowManager(client?.rowManager);
    }

    onInternalPaint() {
        this.attachToProject(this.disabled ? null : this.client.project);
    }

    /**
     * Redraws the line when the project propagation is done
     * @private
     */
    onProjectRefresh() {
        this.scheduleDraw();
    }

    onProjectStatusDateChange() {
        if (this._statusDate === undefined) {
            this.scheduleDraw();
        }
    }

    onTranslateRow({ row }) {
        // a changetotalheight event is fired after translations, if a rowHeight change is detected here it will redraw
        // the line
        if (row.lastTop >= 0 && row.top !== row.lastTop) {
            this.scheduleDraw();
        }
    }

    // Refreshing only lines for dragged task to avoid slowing down drag operation
    onTaskDrag({ taskRecords, dragData }) {
        taskRecords.forEach(record => {
            this.updateLineForTask(record, DateHelper.add(record.startDate, dragData.timeDiff));
        });
    }
    // endregion

    /**
     * Returns true if progress line should be drawn
     * @returns {Boolean}
     */
    shouldDrawProgressLine() {
        const me = this;
        return !me.client.timeAxisSubGrid.collapsed && !me.disabled && (!me.drawLineOnlyWhenStatusDateVisible || me.client.timeAxis.dateInAxis(me.statusDate));
    }

    clearDomConfigs() {
        this.domConfigs.clear();
    }

    /**
     * Returns true if task should be connected to the progress line.
     * @param {Gantt.model.TaskModel} taskRecord
     * @param {Date} [startDate] Provide to check if task record should be connected to the progress line if it'd
     * start then
     * @returns {Boolean}
     * @internal
     */
    isStatusLineTask(taskRecord, startDate) {
        const statusDate = this.statusDate;

        startDate = startDate || taskRecord.startDate;

        // task should be visible and not inactive
        return taskRecord?.project && !taskRecord.inactive && this.client.timeAxis.isTimeSpanInAxis(taskRecord) &&
            // - is in progress
            (taskRecord.isInProgress ||
                // ...or is not started and its start date is before statusDate
                (!taskRecord.isStarted && startDate < statusDate) ||
                // ...or is finished and its start date is after statusDate
                (taskRecord.isCompleted && startDate > statusDate));
    }

    // region Drawing

    /**
     * Returns status date horizontal position relative to the foreground canvas
     * @returns {Number}
     * @private
     */
    getStatusDateX() {
        let { statusDate } = this;

        const { client } = this;

        if (!client.timeAxis.dateInAxis(statusDate)) {
            statusDate = statusDate < client.timeAxis.startDate ? client.timeAxis.startDate : client.timeAxis.endDate;
        }

        return client.getCoordinateFromDate(statusDate);
    }

    /**
     * Returns object with status date local coordinate and view x,y coordinates. Used to convert page coordinates to
     * view local.
     * @returns {{statusDateX: Number, viewXY: number[]}}
     * @internal
     */
    getRenderData() {
        const
            statusDateX   = this.getStatusDateX(),
            { client }    = this,
            // We refer to the DOM to get status date horizontal coordinate (for segmented tasks which are not supported yet)
            // we need to adjust progress bar element box to view/scroll.
            {
                rtl,
                timeAxisViewModel : viewModel,
                timeAxisSubGridElement: subGrid
            }             = client,
            { totalSize } = viewModel,
            viewBox       = subGrid.getBoundingClientRect(),
            viewXY        = [
                rtl
                    ? totalSize - viewBox.width + client.scrollLeft - viewBox.left
                    : client.scrollLeft - viewBox.left,
                -viewBox.top
            ];

        return { statusDateX, viewXY };
    }

    /**
     * Renders the progress line.
     */
    draw() {
        const
            me         = this,
            { client } = me;

        me.clearDomConfigs();

        if (!me.shouldDrawProgressLine()) {
            return;
        }

        if (client.isAnimating) {
            client.ion({
                transitionend() {
                    me.scheduleDraw();
                },
                once : true
            });

            return;
        }

        const data = me.getRenderData();

        client.rowManager.forEach(row => {
            const task = client.store.getById(row.id);
            me.getDomConfig(task, row, data);
        });

        me.domSync();
    }

    /**
     * Updates progress line segment for one task
     * @param {Gantt.model.TaskModel} taskRecord
     * @param {Date} [startDate] When task is being dragged, this date is used as a temporary
     * start date to tell if task should be connected to the progress line.
     * @private
     */
    updateLineForTask(taskRecord, startDate) {
        const me = this;

        if (me.disabled) {
            return;
        }

        const row = me.client.getRowFor(taskRecord);

        if (row) {
            me.getDomConfig(taskRecord, row, me.getRenderData(), null, startDate);

            me.domSync();
        }
    }

    /**
     * This method will calculate point inside task element to be connected with line.
     * @param {Gantt.model.TaskModel} record
     * @param {Number[]} translateBy View xy coordinates to calculate relative point position
     * @param {Core.helper.util.Rectangle} [taskBox] Task element box
     * @returns {Object|undefined} Object containing coordinates for point in progress line, or undefined if no progress bar el is found
     * @private
     */
    calculateCoordinateForTask(record, translateBy, taskBox) {
        const
            { client } = this,
            { rtl }    = client,
            {
                children,
                milestone,
                isCompleted
            }          = record,
            totalSize = client.timeAxisViewModel.totalSize;

        // taskBox is passed during the export where we cannot calculate position of the progress bar
        // element
        if (taskBox) {
            const
                box       = taskBox,
                start     = (rtl ? box.right : box.left),
                end       = rtl
                    ? box.left + box.width * (1 - record.percentDone / 100)
                    : box.right - box.width * (1 - record.percentDone / 100);

            return {
                x : Math.min(milestone && !isCompleted ? start : end, totalSize),
                y : box.top + box.height / 2 + translateBy[1]
            };
        }
        else {
            const
                node          = client.getElementFromTaskRecord(record),
                progressBarEl = milestone ? node : node?.querySelector('.b-task-percent-bar');

            if (progressBarEl) {
                const
                    box       = progressBarEl.getBoundingClientRect(),
                    offsetY   = children?.length ? this.parentOffsetY : 0,
                    start     = (rtl ? box.right : box.left) + translateBy[0],
                    end       = (rtl ? box.left : box.right) + translateBy[0];

                return {
                    x : Math.min(milestone && !isCompleted ? start : end, totalSize),
                    y : box.top + box.height / 2 + translateBy[1] + offsetY
                };
            }
        }
    }

    renderLine(data) {
        return Object.assign({
            tag   : 'line',
            ns    : 'http://www.w3.org/2000/svg',
            // cannot use className when namespace is provided
            class : this.lineCls
        }, data);
    }

    getDomConfig(taskRecord, rowBox, data, taskBox, startDate) {
        const
            me                      = this,
            { statusDateX, viewXY } = data,
            taskId                  = taskRecord.id,
            lineDefinitions         = [];

        let point;

        if (me.isStatusLineTask(taskRecord, startDate)) {
            point = me.calculateCoordinateForTask(taskRecord, viewXY, taskBox);

            // If multiple rows are affected by event update, it could happen, that point
            // could not be resolved
            point && lineDefinitions.push(
                this.renderLine({
                    dataset : { taskId },
                    x1      : statusDateX,
                    y1      : rowBox.top,
                    x2      : point.x,
                    y2      : point.y
                }),
                this.renderLine({
                    dataset : { taskId },
                    x1      : point.x,
                    y1      : point.y,
                    x2      : statusDateX,
                    y2      : rowBox.bottom
                })
            );
        }

        // otherwise we render vertical status line

        if (!point) {
            lineDefinitions.push(
                this.renderLine({
                    dataset : { taskId },
                    x1      : statusDateX,
                    y1      : rowBox.top,
                    x2      : statusDateX,
                    y2      : rowBox.bottom
                })
            );
        }

        me.domConfigs.set(taskRecord.id, lineDefinitions);
    }

    domSync(targetElement = this.svgCanvas, batch = false) {
        const me = this;

        DomSync.sync({
            targetElement,
            domConfig : {
                onlyChildren : true,
                children     : Array.from(me.domConfigs.values()).flat()
            },
            releaseThreshold : 0,
            strict           : true,
            callback() {
                me.client.trigger('progressLineDrawn');
            }
        });

        if (batch) {
            me.clearDomConfigs();
        }
    }

    // endregion
}

GridFeatureManager.registerFeature(ProgressLine);
